import { Injectable } from '@angular/core';
import { Chart } from 'angular-highcharts';
import * as Models from '../../models/report-view.models';
import { LocaleService, TranslationService } from '../../services/services-index';
import { getChartColors } from '../../components/panel-elements/panel-utils';

@Injectable({
  providedIn: 'root'
})
export class BillboardChartService {
  private locale: string;

  constructor(
    private translationService: TranslationService,
    private localeService: LocaleService
  ) {
    this.localeService.locale$.subscribe(loc => this.locale = loc);
  }

  getBillboardChart(
    dataSet: Models.DataSet,
    metricName: string,
    chartType: string,
    settings: Models.ElementSettings
  ): Chart {
    switch (chartType) {
      case 'bar':
        return this.getBarChart(dataSet, metricName, settings);
      
      case 'spline':
        return this.getSplineChart(dataSet, settings);

      default:
        return this.getBarChart(dataSet, metricName, settings);
    }
  }

  getBarChart(
    dataSet: Models.DataSet,
    metricName: string,
    settings: Models.ElementSettings
  ): Chart {
    const data: { x: string; y: number; label: string; color?: string }[] = [];
    const billboardMetric = dataSet.rows[0];

    const getValue = (metric: string): number => {
      const index = dataSet.columns.findIndex(col => col.name === metric);
      const cell = billboardMetric[index];
      if (!cell) console.warn('cell not found', metric);
      return cell?.value as number;
    };

    const getLabel = (metric: string): string => {
      const index = dataSet.columns.findIndex(col => col.name === metric);
      const cell = billboardMetric[index];
      if (!cell) console.warn('cell not found', metric);
      return (cell?.label ?? cell?.value) as string;
    };

    const metricValue = getValue(metricName);
    const benchmarkEntity = settings.dimensionName ?? 'Dealer';
    const metricMode = settings.options?.['mode'] ?? 'Brand';

    const avgMetric = metricMode === 'Brand' ? `${metricName}Brand` : `NationalAvg${metricName}`;
    const top10Metric = metricMode === 'Brand' ? `${metricName}Top10` : `NationalAvg${metricName}Top10`;
    const bottom10Metric = metricMode === 'Brand' ? `${metricName}Bottom10` : `NationalAvg${metricName}Bottom10`;

    const brandAvgLabel = this.translationService.getLabelTranslation(`${benchmarkEntity} Avg.`, this.locale);
    const top10Label = this.translationService.getLabelTranslation('Top 10%', this.locale);
    const bottom10Label = this.translationService.getLabelTranslation('Bottom 10%', this.locale);

    // benchmark bars
    data.push({ x: brandAvgLabel, y: getValue(avgMetric), label: getLabel(avgMetric), color: settings.chartPrimaryColor });
    data.push({ x: top10Label, y: getValue(top10Metric), label: getLabel(top10Metric), color: settings.chartPrimaryColor });
    data.push({ x: bottom10Label, y: getValue(bottom10Metric), label: getLabel(bottom10Metric), color: settings.chartPrimaryColor });

    const gtThanAvg = metricValue > getValue(avgMetric);
    const up = (gtThanAvg && !settings.metricInverted) || (!gtThanAvg && settings.metricInverted);

    if (getLabel('Level') !== 'National') {
      data.push({
        x: 'Selected',
        y: metricValue,
        label: getLabel(metricName),
        color: up ? 'green' : 'red'
      });
    }

    return new Chart({
      chart: { type: 'bar' },
      colors: ['black'],
      title: { text: null },
      xAxis: {
        categories: data.map(val => val.x),
        title: { text: null },
        labels: {
          style: {
            color: '#000',
            fontWeight: 'bold',
            fontFamily: 'Helvetica Neue'
          }
        }
      },
      yAxis: {
        min: 0,
        labels: { enabled: false },
        gridLineWidth: 0,
        title: { text: null }
      },
      tooltip: { enabled: false },
      plotOptions: {
        bar: {
          groupPadding: 0.1,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const point: any = this.point;
              return `<span style="font-weight: 500">${point.label}</span>`;
            },
            style: { fontFamily: 'Helvetica Neue' }
          }
        }
      },
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
      series: [
        {
          type: 'bar',
          data: data.map(val => ({
            y: val.y,
            color: val.color,
            label: val.label
          }))
        }
      ]
    });
  }
  

  getSplineChart(
    dataSet: Models.DataSet,
    settings: Models.ElementSettings): Chart {
    const colors = getChartColors(settings);

    const seriesData = dataSet.rows.map(row => {
      const dateCell = row[0]; // assuming first column is date
      const metricCell = row[1]; // assuming second column is value

      return {
        name: dateCell.label ?? dateCell.value.toString(),
        displayValue: metricCell.label ?? metricCell.value.toString(),
        y: typeof metricCell.value === 'number' ? metricCell.value : parseFloat(metricCell.value as string)
      };
    });

    return new Chart({
      chart: {
        type: 'spline'
      },
      colors: ['black'],
      title: {
        text: null
      },
      xAxis: {
        categories: seriesData.map(val => val.y.toString()),
        labels: {
          enabled: false
        },
      },
      yAxis: {
        min: 0,
        labels: {
          enabled: false
        },
        gridLineWidth: 0,
        title: {
          text: null
        }
      },
      tooltip: {
        shared: false,
        pointFormat: '<b>{point.y:,.0f}<br/>'
      },
      plotOptions: {
        series: {
          color: colors[0],
        }
      },
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      exporting: { enabled: false },
      series: [{
        type: 'spline',
        data: seriesData
      } as Highcharts.SeriesSplineOptions]
    });
  }
}
